/*
 * safety-cover-builder.js
 * Custom element for sections/safety-cover-builder.liquid ("Build my cover").
 *
 * Contract with the Liquid section:
 *   - All variant data arrives in a <script type="application/json" data-sc-variants>
 *     blob. Prices are PRE-FORMATTED by the Liquid `money` filter. This file never
 *     formats, computes, or infers a price. If a price is not in the blob, there is
 *     no price to show.
 *   - Every question is already in the DOM at page load. Nothing is fetched.
 *   - The single source of truth for "does this combination exist" is the variant
 *     blob itself. A missing combination is simply absent. No second availability list.
 *   - The <select name="id"> inside the product form is the real input. With JS off it
 *     is a working variant picker; on init we hide it and drive it from render().
 *
 * State: five keys, one per question. Every answer writes one key and calls render().
 * Keep logic inside render(). Do not spread it across click handlers.
 */
(function () {
  if (customElements.get('safety-cover-builder')) return;

  var FABRIC_KEYS = ['royal', 'king', 'emperor'];

  function norm(v) {
    return (v == null ? '' : String(v)).toLowerCase();
  }

  // "Royal Mesh" -> "royal". Matching on substrings keeps this working if merch
  // renames an option value in admin ("King99 Mesh", "King 99 Mesh", ...).
  function fabricKey(value) {
    var v = norm(value);
    for (var i = 0; i < FABRIC_KEYS.length; i++) {
      if (v.indexOf(FABRIC_KEYS[i]) !== -1) return FABRIC_KEYS[i];
    }
    return v.replace(/[^a-z0-9]/g, '');
  }

  // "12' x 24'" -> [12, 24]. "5x5" -> [5, 5].
  function dims(value) {
    var m = norm(value).match(/\d+(?:\.\d+)?/g);
    if (!m || m.length < 2) return null;
    return [parseFloat(m[0]), parseFloat(m[1])];
  }

  function dimKey(value) {
    var d = dims(value);
    return d ? d[0] + 'x' + d[1] : norm(value).replace(/[^a-z0-9]/g, '');
  }

  customElements.define(
    'safety-cover-builder',
    class SafetyCoverBuilder extends HTMLElement {
      connectedCallback() {
        // The element can upgrade before its children are parsed (happens to any
        // instance after the first, and to the first if the script is not deferred).
        // Wait until the markup we need actually exists.
        if (this.initialised) return;
        var self = this;
        if (this.querySelector('[data-sc-price]')) {
          this.init();
        } else if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', function () {
            self.init();
          });
        } else {
          requestAnimationFrame(function () {
            self.init();
          });
        }
      }

      // Every bail-out below leaves the no-JS fallback in place: the builder stays
      // hidden and the native variant picker stays visible, so the product is still
      // buyable. That degrades quietly on a live page, which is right, but it looks
      // identical to "the script never loaded" while the page is being built. Say
      // which one it was.
      bail(why) {
        if (window.console && console.warn) console.warn('[safety-cover-builder] ' + why);
      }

      init() {
        if (this.initialised) return;
        var blob = this.querySelector('[data-sc-variants]');
        if (!blob) return this.bail('no [data-sc-variants] blob in the section. The Liquid rendered its no-product placeholder, or the markup changed.');
        try {
          this.variants = JSON.parse(blob.textContent);
        } catch (e) {
          return this.bail('the variant blob is not valid JSON: ' + e.message + '. Check the shop money format for HTML, and the option values for anything the `json` filter did not escape.');
        }
        if (!this.variants || !this.variants.length) {
          return this.bail('the variant blob is empty. The product in the section settings has no variants the storefront can see - check it is Active and published to Online Store.');
        }
        this.initialised = true;

        this.state = { mode: null, grid: null, steps: null, fab: null, size: null };

        this.form = this.querySelector('form');
        this.nativeSelect = this.querySelector('[data-sc-native]');
        this.fallback = this.querySelector('[data-sc-fallback]');
        this.builder = this.querySelector('[data-sc-builder]');
        this.sizeSelect = this.querySelector('[data-sc-size-select]');
        this.helper = this.querySelector('[data-sc-helper]');
        this.priceEl = this.querySelector('[data-sc-price]');
        this.specEl = this.querySelector('[data-sc-spec]');
        this.anchorEl = this.querySelector('[data-sc-anchor]');
        this.submit = this.querySelector('[data-sc-submit]');
        this.quoteCta = this.querySelector('[data-sc-quote]');
        this.sticky = this.querySelector('[data-sc-sticky]');
        this.terms = this.querySelector('[data-sc-terms]');

        // Index the variants: fabric -> spacing -> size.
        this.index = {};
        this.sizeOrder = [];
        var i;
        for (i = 0; i < this.variants.length; i++) {
          var v = this.variants[i];
          v.fabKey = fabricKey(v.fabric);
          v.gridKey = dimKey(v.spacing);
          v.sizeKey = dimKey(v.size);
          if (!this.index[v.fabKey]) this.index[v.fabKey] = {};
          if (!this.index[v.fabKey][v.gridKey]) this.index[v.fabKey][v.gridKey] = {};
          this.index[v.fabKey][v.gridKey][v.sizeKey] = v;
          if (this.sizeOrder.indexOf(v.sizeKey) === -1) this.sizeOrder.push(v.sizeKey);
        }
        this.byId = {};
        for (i = 0; i < this.variants.length; i++) this.byId[String(this.variants[i].id)] = this.variants[i];

        // Progressive enhancement: the native picker was the input until now.
        if (this.fallback) this.fallback.setAttribute('hidden', '');
        if (this.builder) this.builder.removeAttribute('hidden');

        this.bind();
        this.restoreFromUrl();
        this.render();
      }

      bind() {
        var self = this;

        // One delegated listener on the section root. Not a listener per button.
        this.addEventListener('click', function (evt) {
          var btn = evt.target.closest('[data-key]');
          if (!btn || !self.contains(btn)) return;
          evt.preventDefault();
          self.answer(btn.dataset.key, btn.dataset.answer, btn);
        });

        if (this.sizeSelect) {
          this.sizeSelect.addEventListener('change', function () {
            self.state.size = this.value || null;
            if (this.value) self.setHelper(self.dataset.helperSize);
            self.render();
          });
        }

        var stickyAdd = this.querySelector('[data-sc-sticky-add]');
        if (stickyAdd && this.form) {
          stickyAdd.addEventListener('click', function () {
            if (self.form.requestSubmit) self.form.requestSubmit();
            else self.form.querySelector('[type="submit"]').click();
          });
        }
      }

      answer(key, value, btn) {
        // "Not sure" style exits: scroll away, change no state.
        if (key === 'exit') {
          var target = document.querySelector(value);
          if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }

        // Fabric and spacing buttons carry the product's raw option value ("Royal Mesh",
        // "5x5"), so the markup can never drift from the variants. Normalise here.
        this.state[key] = this.normalise(key, value);

        var group = btn.closest('[data-sc-step]');
        if (group) {
          var sibs = group.querySelectorAll('[data-key="' + key + '"]');
          for (var i = 0; i < sibs.length; i++) sibs[i].classList.remove('is-selected');
          btn.classList.add('is-selected');
        }

        // Changing fabric or spacing can invalidate the chosen size.
        if (key === 'fab' || key === 'grid') {
          if (this.state.size && !this.lookup(this.state.fab, this.state.grid, this.state.size)) {
            this.state.size = null;
          }
        }

        if (key === 'mode') this.setHelper(value === 'new' ? this.dataset.helperNew : this.dataset.helperRepl);
        if (key === 'grid') this.setHelper(this.dataset.helperSteps);
        if (key === 'steps') this.setHelper(this.dataset.helperFab);
        if (key === 'fab') this.setHelper(this.dataset.helperPickSize);

        this.render();
        this.revealNext(key);
      }

      normalise(key, value) {
        if (key === 'fab') return fabricKey(value);
        if (key === 'grid' || key === 'size') return dimKey(value);
        return value;
      }

      lookup(fab, grid, size) {
        if (!fab || !grid || !size) return null;
        return (this.index[fab] && this.index[fab][grid] && this.index[fab][grid][size]) || null;
      }

      setHelper(text) {
        if (this.helper && text) this.helper.textContent = text;
      }

      // Which step follows the one just answered. Question 1 branches.
      revealNext(key) {
        var next = null;
        if (key === 'mode') next = this.state.mode === 'new' ? 'grid-new' : 'grid-repl';
        else if (key === 'grid') next = 'steps';
        else if (key === 'steps') next = 'fab';
        else if (key === 'fab') next = 'size';
        if (!next) return;
        var el = this.querySelector('[data-sc-step="' + next + '"]');
        if (!el) return;
        el.classList.add('is-active');
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        var focusTarget = el.querySelector('h3, [data-key], select');
        if (focusTarget) {
          focusTarget.setAttribute('tabindex', '-1');
          focusTarget.focus({ preventScroll: true });
        }
      }

      // Rebuild the size dropdown from the variant data: only sizes the chosen
      // fabric + spacing can actually be built in, price shown inline.
      fillSizes() {
        if (!this.sizeSelect) return;
        var fab = this.state.fab;
        var grid = this.state.grid;
        if (!fab || !grid) return;

        var current = this.state.size;
        this.sizeSelect.innerHTML = '';
        var placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = this.dataset.sizePlaceholder || 'Choose your pool size';
        this.sizeSelect.appendChild(placeholder);

        for (var i = 0; i < this.sizeOrder.length; i++) {
          var key = this.sizeOrder[i];
          var v = this.lookup(fab, grid, key);
          if (!v) continue; // Not manufactured in this combination. It never appears.
          var opt = document.createElement('option');
          opt.value = key;
          opt.textContent = v.size + '   ' + v.price;
          if (v.available === false) opt.disabled = true;
          this.sizeSelect.appendChild(opt);
        }
        this.sizeSelect.value = current && this.lookup(fab, grid, current) ? current : '';
      }

      render() {
        var s = this.state;

        // Q1 branches. Only one version of Q2 is ever on screen, even if the shopper
        // goes back and changes the answer.
        var newBranch = this.querySelector('[data-sc-step="grid-new"]');
        var replBranch = this.querySelector('[data-sc-step="grid-repl"]');
        if (s.mode && newBranch && replBranch) {
          (s.mode === 'new' ? replBranch : newBranch).classList.remove('is-active');
        }

        if (s.fab && s.grid) this.fillSizes();

        // Line item properties. Q1 and Q3 are not variant options, they ride along.
        this.setProperty('order_type', s.mode ? this.optionLabel('mode', s.mode) : '');
        this.setProperty('steps', s.steps ? this.optionLabel('steps', s.steps) : '');

        var variant = this.lookup(s.fab, s.grid, s.size);
        var resolvable = !!(s.fab && s.grid && s.size);

        if (!resolvable) {
          this.setPrice(this.dataset.pricePlaceholder || '', this.dataset.specPlaceholder || '');
          this.setBuyable(false, false);
          this.setUrlVariant(null);
          return;
        }

        if (!variant) {
          // Fallback only. The dropdown filter should make this unreachable.
          this.setPrice(this.dataset.quotePriceLabel || 'By quote', this.dataset.quoteSpec || '');
          this.setBuyable(false, true);
          this.setUrlVariant(null);
          return;
        }

        var spec = variant.fabric + ', ' + variant.spacing + ' spacing, ' + variant.size + ' pool';
        this.setPrice(variant.price, spec);
        this.setAnchors(variant);
        this.setBuyable(variant.available !== false, false);

        if (this.nativeSelect) {
          this.nativeSelect.value = String(variant.id);
          this.nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
        }
        this.setUrlVariant(variant.id);
      }

      optionLabel(key, value) {
        var btn = this.querySelector('[data-key="' + key + '"][data-answer="' + value + '"]');
        return btn ? btn.dataset.label || btn.querySelector('.sc-opt__label').textContent.trim() : value;
      }

      setProperty(name, value) {
        var input = this.querySelector('[data-sc-prop="' + name + '"]');
        if (!input) return;
        input.value = value;
        // A blank property would still post an empty line on the order.
        input.disabled = !value;
      }

      setPrice(price, spec) {
        if (this.priceEl) this.priceEl.textContent = price;
        if (this.specEl) this.specEl.textContent = spec;
        var sp = this.querySelector('[data-sc-sticky-price]');
        var ss = this.querySelector('[data-sc-sticky-spec]');
        if (sp) sp.textContent = price;
        if (ss) ss.textContent = spec;
      }

      // Cover perimeter / spacing, rounded up. Labelled an estimate on the page and
      // confirmed on the signoff drawing.
      setAnchors(variant) {
        if (!this.anchorEl) return;
        var pool = dims(variant.size);
        var space = dims(variant.spacing);
        if (!pool || !space) {
          this.anchorEl.textContent = '';
          return;
        }
        var perimeter = 2 * (pool[0] + 2 + (pool[1] + 2));
        var count = Math.ceil(perimeter / space[0]);
        var tpl = this.dataset.anchorTemplate || 'Estimated anchors: about [count].';
        this.anchorEl.textContent = tpl.replace('[count]', count);
      }

      setBuyable(canBuy, quoteOnly) {
        if (this.submit) {
          this.submit.disabled = !canBuy;
          this.submit.hidden = quoteOnly;
        }
        if (this.quoteCta) this.quoteCta.hidden = !quoteOnly;
        if (this.terms) this.terms.hidden = !canBuy;
        if (this.sticky) this.sticky.classList.toggle('is-visible', canBuy || quoteOnly);
        var stickyAdd = this.querySelector('[data-sc-sticky-add]');
        if (stickyAdd) stickyAdd.disabled = !canBuy;
      }

      // A refresh or a shared link keeps the selection.
      setUrlVariant(id) {
        if (!window.history || !window.history.replaceState) return;
        var url = new URL(window.location.href);
        if (id) url.searchParams.set('variant', id);
        else url.searchParams.delete('variant');
        window.history.replaceState({}, '', url.toString());
      }

      restoreFromUrl() {
        var id = new URL(window.location.href).searchParams.get('variant');
        var variant = id && this.byId[String(id)];
        if (!variant) return;

        this.state.fab = variant.fabKey;
        this.state.grid = variant.gridKey;
        this.state.size = variant.sizeKey;

        // Mark the matching buttons and open every step so the shopper can still
        // answer the two questions that are not encoded in the variant.
        this.markSelected('fab', variant.fabKey);
        this.markSelected('grid', variant.gridKey);
        var steps = this.querySelectorAll('[data-sc-step]');
        for (var i = 0; i < steps.length; i++) {
          if (steps[i].dataset.scStep !== 'grid-repl') steps[i].classList.add('is-active');
        }
        this.fillSizes();
        if (this.sizeSelect) this.sizeSelect.value = variant.sizeKey;
      }

      markSelected(key, value) {
        var btns = this.querySelectorAll('[data-key="' + key + '"]');
        for (var i = 0; i < btns.length; i++) {
          btns[i].classList.toggle('is-selected', this.normalise(key, btns[i].dataset.answer) === value);
        }
      }
    }
  );
})();
